import { getUserByEmail, useBuilding } from "@/api";
import PageBackButton from "@/components/PageBackButton";
import { PersonSearch } from "@/components/PersonSearch";
import { usePrompt } from "@/hooks/usePrompt";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";
import { User } from "@/types";
import { Breadcrumbs } from "@app-components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import bookingStyles from "@/styles/Bookings.module.scss";
import { AnimatePresence, motion } from "framer-motion";
import SnackOrderForm from "@/components/snack/order_form";

export const Route = createFileRoute("/_portal/snack-admin/orders/new")({
  component: NewFoodOrder,
});

function NewFoodOrder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allowSnackAdminAccess } = useWelcomeSnackAccess();
  const { data: building } = useBuilding();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const hasOrdered = useRef(false);

  usePrompt({
    isOpen: !!selectedUser && !hasOrdered.current,
  });

  const breadcrumbs = [
    {
      label: t("SIDEBAR.SNACK_ORDERS"),
      path: "/snack-admin/orders",
    },
    {
      label: t("SNACK_ADMIN.NEW_ORDER.HEADER"),
      path: "/snack-admin/orders/new",
    },
  ];

  const searchForUser = async (text: string) => {
    if (!text) {
      return [];
    }
    setIsSearching(true);
    try {
      return await getUserByEmail(text);
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  if (!allowSnackAdminAccess) return null;
  return (
    <div
      className={`pageWithoutBottomSpacing ${bookingStyles.container}`}
      style={{ paddingBottom: "var(--wlcm-spacing-xl)" }}
    >
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/snack-admin/orders",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>{t("SNACK_ADMIN.NEW_ORDER.HEADER")}</h1>
        <p>
          {t("SNACK_ADMIN.NEW_ORDER.SUBHEADER", {
            buildingName: building.displayName,
          })}{" "}
          <strong>{t("SNACK_ADMIN.NEW_ORDER.ADMIN_PRIVILEGES")}</strong>
        </p>
      </div>
      <div
        style={{
          width: "max-content",
          minWidth: "40%",
          maxWidth: "100%",
          margin: "0 auto",
        }}
      >
        <PersonSearch
          required
          showCompany
          label={t("SNACK_ADMIN.NEW_ORDER.ON_BEHALF_OF.LABEL")}
          noResultsText={t("SNACK_ADMIN.NEW_ORDER.ON_BEHALF_OF.NO_RESULTS")}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          isSearching={isSearching}
          searchForUser={searchForUser}
          addUser={(user) => setSelectedUser(user)}
          userAdded={!!selectedUser}
          deleteHost={() => {
            setSelectedUser(undefined);
          }}
          infoText={t("SNACK_ADMIN.NEW_ORDER.ON_BEHALF_OF.INFO")}
        />
      </div>
      <div
        style={{
          marginTop: "var(--wlcm-spacing-md)",
        }}
      >
        <AnimatePresence initial={false} mode="wait">
          {selectedUser ? (
            <motion.div
              key="selected"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
            >
              <SnackOrderForm
                onBehalfOfUser={selectedUser}
                onSuccess={() => {
                  hasOrdered.current = true;
                  navigate({ to: "/snack-admin/orders" });
                }}
              />
            </motion.div>
          ) : (
            <motion.div
              key="not-selected"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
            >
              <div
                style={{
                  textAlign: "center",
                  margin: "var(--wlcm-spacing-lg) 0",
                  opacity: 0.7,
                }}
              >
                <h3>{t("SNACK_ADMIN.NEW_ORDER.NO_USER_SELECTED.HEADER")}</h3>
                <p>{t("SNACK_ADMIN.NEW_ORDER.NO_USER_SELECTED.BODY")}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
